import { useEffect, useRef, useState } from "react";
import { Send, Bot, User, Sparkles, Flame, Clock, Snowflake, AlarmClock, CheckCheck, Package, TicketPercent, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, timeWIB, formatRp } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { btnMint, btnGhost } from "../../lib/ui";

export function ChatArea({ chat, messages, setMessages, processing, onChatUpdate }) {
  const [text, setText] = useState("");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [panel, setPanel] = useState(null);
  const [panelItems, setPanelItems] = useState([]);
  const [panelQ, setPanelQ] = useState("");
  const [confirmItem, setConfirmItem] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, processing]);

  const send = async (msg) => {
    const body = (msg ?? text).trim();
    if (!body || !chat?.id) return;
    try {
      const { data } = await api.post(`/chats/${chat.id}/messages`, { text: body });
      if (data) setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      setText("");
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggleHandoff = async () => {
    try {
      const { data } = await api.post(`/chats/${chat.id}/handoff`, { active: !chat.handoff });
      onChatUpdate(data);
      toast.success(data.handoff ? "Anda mengambil alih percakapan" : "AI melanjutkan percakapan");
    } catch (err) { toast.error(apiError(err)); }
  };

  const setInterest = async (level) => {
    const { data } = await api.post(`/chats/${chat.id}/interest`, { level });
    onChatUpdate(data);
  };

  const remind = async (minutes) => {
    await api.post(`/chats/${chat.id}/reminders`, { minutes });
    setReminderOpen(false);
    toast.success(`Pengingat follow-up dalam ${minutes} menit dibuat`);
  };

  const openPanel = async (type) => {
    setPanel(type);
    setPanelQ("");
    setConfirmItem(null);
    const { data } = await api.get(type === "product" ? "/products" : "/promos");
    setPanelItems(data);
  };

  const sendItem = async () => {
    try {
      const { data } = await api.post(`/chats/${chat.id}/send-${panel}`, { code: confirmItem.code });
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      toast.success(panel === "product" ? "Produk dikirim ke pelanggan" : "Promo dikirim ke pelanggan");
      setPanel(null);
      setConfirmItem(null);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const filteredItems = panelItems.filter((i) =>
    (i.name + i.code).toLowerCase().includes(panelQ.toLowerCase()));

  let lastDate = "";

  return (
    <section className="flex h-screen min-w-0 flex-1 flex-col">
      <header className="flex h-[60px] shrink-0 items-center gap-3 border-b border-white/5 bg-panel px-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-panel3 text-xs font-semibold text-mint">
          {(chat.name || chat.phone || "?").slice(0, 2).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{chat.name || chat.phone}</p>
          <p className="font-mono text-[11px] text-faint">{chat.phone}</p>
        </div>
        <span data-testid="chat-handler-badge" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
          chat.handoff ? "bg-amber/15 text-amber" : "bg-mint/15 text-mint"}`}>
          {chat.handoff ? <User size={12} /> : <Bot size={12} />}
          {chat.handoff ? "Ditangani Operator" : "Ditangani Assistant"}
        </span>
      </header>

      <div className="chat-wall flex-1 space-y-1.5 overflow-y-auto px-6 py-4" data-testid="chat-messages">
        {messages.map((m) => {
          const d = new Date(m.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long" });
          const showDate = d !== lastDate;
          lastDate = d;
          const outgoing = m.role === "assistant" || m.role === "operator";
          return (
            <div key={m.id}>
              {showDate && (
                <div className="my-3 flex justify-center">
                  <span className="rounded-full border border-white/5 bg-panel2 px-3 py-1 font-mono text-[10px] text-faint">{d}</span>
                </div>
              )}
              {m.role === "system" ? (
                <div className="my-2 flex justify-center">
                  <span className="rounded-full bg-amber/10 px-3 py-1 text-[11px] text-amber">{m.text}</span>
                </div>
              ) : (
                <div className={`flex ${outgoing ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] px-3.5 py-2 text-sm shadow-msg ${
                    outgoing ? "rounded-xl rounded-br-sm bg-mint-deep ring-1 ring-mint/20" : "rounded-xl rounded-bl-sm bg-panel2"}`}>
                    {m.type === "image" && m.image_url && <img src={m.image_url} alt="" className="mb-1.5 w-full rounded-lg" />}
                    <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                    <p className="mt-1 flex items-center justify-end gap-1 font-mono text-[10px] text-faint">
                      {m.role === "operator" && <span className="text-amber">operator ·</span>}
                      {timeWIB(m.created_at)}
                      {outgoing && <CheckCheck size={12} className="text-mint" />}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {processing && (
          <div className="flex justify-end">
            <div className="flex gap-1.5 rounded-xl rounded-br-sm bg-mint-deep px-4 py-3 ring-1 ring-mint/20">
              {[0, 1, 2].map((i) => (
                <span key={i} className="h-1.5 w-1.5 animate-bounce-dot rounded-full bg-mint" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {chat.suggestions?.length > 0 && (
        <div className="mx-6 mb-2 rounded-xl border border-mint/15 bg-mint/10 p-3" data-testid="ai-suggestions">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-mint"><Sparkles size={12} /> Saran balasan AI</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {chat.suggestions.map((s, i) => (
              <button key={i} data-testid={`suggestion-pill-${i}`} onClick={() => send(s)}
                className="rounded-full border border-mint/25 bg-panel2 px-3 py-1.5 text-xs text-paper transition-colors hover:border-mint/60">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="shrink-0 border-t border-white/5 bg-panel px-5 py-3">
        <div className="flex items-center gap-2 pb-3">
          <button data-testid="handoff-toggle-btn" onClick={toggleHandoff}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              chat.handoff ? "bg-amber text-ink" : "border border-white/10 text-muted hover:text-paper"}`}>
            <User size={12} /> {chat.handoff ? "Akhiri Handoff" : "Ambil Alih (Human)"}
          </button>
          <button data-testid="reminder-btn" onClick={() => setReminderOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-muted transition-colors hover:text-paper">
            <AlarmClock size={12} /> Ingatkan
          </button>
          <button data-testid="quick-product-btn" onClick={() => openPanel("product")}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-muted transition-colors hover:text-paper">
            <Package size={12} /> Produk
          </button>
          <button data-testid="quick-promo-btn" onClick={() => openPanel("promo")}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-muted transition-colors hover:text-paper">
            <TicketPercent size={12} /> Promo
          </button>
          <span className="mx-1 h-5 w-px bg-white/10" />
          {[["hot", Flame, "bg-mint text-ink", "text-mint"], ["medium", Clock, "bg-amber text-ink", "text-amber"], ["cold", Snowflake, "bg-slate text-ink", "text-slate"]].map(
            ([lvl, Icon, activeCls, idleCls]) => (
              <button key={lvl} data-testid={`interest-toggle-${lvl}`} onClick={() => setInterest(lvl)} title={`Tandai ${lvl}`}
                className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  chat.interest_level === lvl ? activeCls : `border border-white/10 ${idleCls} hover:border-white/30`}`}>
                <Icon size={13} />
              </button>
            )
          )}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
          <input data-testid="operator-message-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={chat.handoff ? "Ketik balasan manual…" : "Kirim manual (AI tetap aktif)…"}
            className="flex-1 rounded-full bg-ink px-4 py-2.5 text-sm text-paper ring-1 ring-white/10 placeholder:text-faint focus:outline-none focus:ring-mint" />
          <button data-testid="operator-send-btn" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-mint text-ink transition-transform hover:scale-105">
            <Send size={16} />
          </button>
        </form>
      </div>

      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="max-w-sm border-white/10 bg-panel2 text-paper sm:rounded-2xl">
          <DialogHeader><DialogTitle>Ingatkan follow-up</DialogTitle></DialogHeader>
          <div className="flex gap-3">
            {[[5, "5 menit"], [30, "30 menit"], [60, "1 jam"]].map(([m, l]) => (
              <button key={m} data-testid={`reminder-quick-${m}`} onClick={() => remind(m)} className={`${btnGhost} flex-1`}>{l}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!panel} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent className="max-w-md border-white/10 bg-panel2 text-paper sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle>{confirmItem ? "Konfirmasi kirim" : panel === "product" ? "Kirim Produk" : "Kirim Promo"}</DialogTitle>
          </DialogHeader>
          {!confirmItem ? (
            <>
              <div className="relative">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-faint" />
                <input data-testid="quick-panel-search" autoFocus value={panelQ} onChange={(e) => setPanelQ(e.target.value)}
                  placeholder={panel === "product" ? "Cari produk…" : "Cari promo…"}
                  className="w-full rounded-full border border-white/10 bg-ink py-2 pl-9 pr-4 text-sm text-paper placeholder:text-faint focus:border-mint/50 focus:outline-none" />
              </div>
              <div className="max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="quick-panel-list">
                {filteredItems.map((item) => (
                  <button key={item.code} data-testid={`quick-item-${item.code}`} onClick={() => setConfirmItem(item)}
                    className="flex w-full items-center gap-3 rounded-xl border border-white/5 bg-panel p-3 text-left transition-colors hover:border-mint/40">
                    {panel === "product" ? (
                      item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                      ) : (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-panel3"><Package size={15} className="text-mint" /></span>
                      )
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-mint/10"><TicketPercent size={15} className="text-mint" /></span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{item.name}</p>
                      <p className="truncate text-[11px] text-faint">{item.code}{item.description ? ` · ${item.description}` : ""}</p>
                    </div>
                    {panel === "product" && <span className="shrink-0 font-mono text-xs text-mint">{formatRp(item.price)}</span>}
                  </button>
                ))}
                {!filteredItems.length && <p className="py-4 text-center text-sm text-faint">Tidak ada hasil.</p>}
              </div>
            </>
          ) : (
            <div>
              <div className="flex items-center gap-3 rounded-xl border border-mint/20 bg-mint/5 p-4">
                {panel === "product" && confirmItem.image_url && (
                  <img src={confirmItem.image_url} alt={confirmItem.name} className="h-14 w-14 rounded-lg object-cover" />
                )}
                <div className="min-w-0">
                  <p className="font-semibold">{confirmItem.name}</p>
                  {panel === "product" ? (
                    <p className="font-mono text-sm text-mint">{formatRp(confirmItem.price)}</p>
                  ) : (
                    <p className="text-xs text-muted">{confirmItem.description}</p>
                  )}
                </div>
              </div>
              <p className="mt-3 text-xs text-muted">
                Akan dikirim ke <span className="font-mono text-paper">{chat.name || chat.phone}</span> sebagai pesan WhatsApp.
              </p>
              <div className="mt-4 flex gap-3">
                <button data-testid="quick-panel-cancel" onClick={() => setConfirmItem(null)} className={`${btnGhost} flex-1`}>Batal</button>
                <button data-testid="quick-panel-send" onClick={sendItem} className={`${btnMint} flex-1`}>Kirim</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
